import { useState, useContext, useEffect } from 'react';
import { StoreContext } from '../../../../Store';
import { Modal, Popover, PopoverContent, OverlayTrigger } from 'react-bootstrap';
import DataTable, { useDataTableHandle } from '../../../../DataTable';
import Tooltip from '../../../../Tooltip/Tooltip';
import {
  convertToGbp,
  calculateInvoiceTotal,
  validatePreferenceData,
  isRexNumberRequired,
  isEuImportersKnowledgeClaim,
} from '../../utils';
import linkIcon from '../../../../../images/link.png';
import './PreferenceCheckModal.css';
import { useDropdownOptions } from '../../../queryHooks/useDropdownOptions';
import { useQaItemsActions } from '../../../queryHooks/useQaItemsApis';
import { getIsValidPreferenceTransitMrn, TRANSIT_MRN_VALIDATION_MESSAGE } from '../../../../../utils/stringValidation';

const Warning = ({ errorMessage }) => (
  <OverlayTrigger
    placement="top"
    trigger={['hover', 'focus']}
    popperConfig={{ modifiers: [{ name: 'offset', options: { offset: [0, 15] } }] }}
    overlay={
      <Popover style={{ minWidth: 'auto' }}>
        <PopoverContent>
          <p>{errorMessage}</p>
        </PopoverContent>
      </Popover>
    }
  >
    <p className="bp-warning-paragraph">
      <span className="warning bp-warning"></span>
    </p>
  </OverlayTrigger>
);

const RenderTooltip = ({ content, minWidth }) => {
  return (
    <Tooltip style={{ minWidth: minWidth }}>
      <p className="text-center">{content}</p>
    </Tooltip>
  );
};

const getPreferenceValue = (preferenceData = [], invoiceNumber, origin, field) => {
  const [preference = {}] = preferenceData.filter(
    (item) => item.invoiceNumber.toUpperCase() === invoiceNumber.toUpperCase() && item.origin === origin
  );
  return preference ? preference[field] : null;
};

/** Playwright / stable selectors: {prefix}-{origin}-{invoiceNumber} */
const rowTestId = (prefix, origin, invoiceNumber) => `${prefix}-${origin}-${invoiceNumber}`;

const RenderTable = ({
  title,
  dataTableHandle,
  idKey,
  data,
  preferenceOptions,
  handlePreferenceChange,
  preferenceData,
  isReadOnly,
}) => {
  const isThirdParty = idKey === 'third-country-preference';

  return (
    <>
      <Modal.Header>
        <Modal.Title className="d-flex align-items-center gap-3">
          <span>{title}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <DataTable
          handle={dataTableHandle}
          dontRefreshPagination
          idKey={idKey}
          data={data}
          className="table-striped"
          showAdvnSearch={false}
          sortable={false}
          filterable={false}
          showLowerCaseHeaders
        >
          <DataTable.Row style={{ fontSize: '14px' }} />
          <DataTable.Column colKey="invoiceNumber" header="Invoice" className="align-middle">
            {({ invoiceNumber, origin }) => (
              <span data-testid={rowTestId('invoiceNumber', origin, invoiceNumber)}>{invoiceNumber}</span>
            )}
          </DataTable.Column>
          <DataTable.Column
            colKey="value"
            header="Value"
            className="align-middle"
            tooltip={
              <RenderTooltip
                content={
                  <>
                    This does not include additions and deductions.
                    <br />
                    Check invoice for total invoice amount.
                  </>
                }
                minWidth={350}
              />
            }
          >
            {({ value, invoiceNumber, origin }) => (
              <span data-testid={rowTestId('invoiceValue', origin, invoiceNumber)}>{value ? `£${value}` : 'N/A'}</span>
            )}
          </DataTable.Column>
          <DataTable.Column colKey="origin" header="Origin" className="align-middle">
            {({ invoiceNumber, origin }) => (
              <span data-testid={rowTestId('origin', origin, invoiceNumber)}>{origin}</span>
            )}
          </DataTable.Column>
          <DataTable.Column colKey="preference" header="Preference" className="align-middle" maxWidth="28ch">
            {({ invoiceNumber, origin }) => {
              const fieldName = isThirdParty ? 'thirdPreferenceClaim' : 'EUPreferenceClaim';
              const preferenceValue = getPreferenceValue(preferenceData, invoiceNumber, origin, fieldName);
              const isValid = !!preferenceValue;

              return (
                <select
                  name={fieldName}
                  data-testid={rowTestId('preference', origin, invoiceNumber)}
                  value={preferenceValue || ''}
                  onChange={(e) => {
                    handlePreferenceChange(e, invoiceNumber, origin);
                  }}
                  className={isValid ? '' : 'qa-field-error'}
                  disabled={isReadOnly}
                >
                  <option value="">- SELECT -</option>
                  {preferenceOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.PreferenceClaimValue}
                    </option>
                  ))}
                </select>
              );
            }}
          </DataTable.Column>
          {!isThirdParty && (
            <DataTable.Column
              colKey="rexNumber"
              header="REX Number"
              className="align-middle"
              maxWidth="20ch"
              tooltip={
                <RenderTooltip
                  content="REX number is required when an invoice value is above €6,000 or £5,400"
                  minWidth={350}
                />
              }
            >
              {({ invoiceNumber, origin, value }) => {
                const preferenceClaimId = getPreferenceValue(
                  preferenceData,
                  invoiceNumber,
                  origin,
                  'EUPreferenceClaim'
                );
                const rexValue = getPreferenceValue(preferenceData, invoiceNumber, origin, 'rexNumber');
                const rexError = getPreferenceValue(preferenceData, invoiceNumber, origin, 'rexError');
                const isU117 = isEuImportersKnowledgeClaim(preferenceClaimId, preferenceOptions);
                const isRequired = isRexNumberRequired(value, preferenceClaimId, preferenceOptions);
                const displayRexValue = isU117 ? '' : rexValue || '';
                const isValid = displayRexValue || !isRequired;
                return (
                  <div className="rex-number-field">
                    <input
                      name="rexNumber"
                      type="text"
                      data-testid={rowTestId('rexInput', origin, invoiceNumber)}
                      placeholder={isRequired ? 'REX Number Required' : 'Not Required'}
                      onChange={(e) => {
                        handlePreferenceChange(e, invoiceNumber, origin);
                      }}
                      className={!isValid || (rexError && !isU117) ? 'qa-field-error' : ''}
                      value={displayRexValue}
                      disabled={isReadOnly || isU117}
                    />
                    <div className="rex-number-field__icons">
                      {rexError && !!displayRexValue && (
                        <div className="validate-rex-error">
                          <Warning errorMessage={!!displayRexValue ? 'Invalid REX Number' : 'Rex Number required'} />
                        </div>
                      )}
                      {!!displayRexValue && (
                        <div className="validate-rex">
                          <a
                            data-testid={rowTestId('rexCheckLink', origin, invoiceNumber)}
                            href={`https://ec.europa.eu/taxation_customs/dds2/eos/rex_validation.jsp?Number=${displayRexValue}&Type=REX&Lang=en&Expand=true`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <img src={linkIcon} alt="Validate REX Number" title="Validate REX Number" />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                );
              }}
            </DataTable.Column>
          )}
          {isThirdParty && (
            <DataTable.Column
              colKey="directTransportRules"
              header="Direct Transport Rules"
              className="align-middle"
              maxWidth="20ch"
              hidden={!isThirdParty}
              tooltip={
                <RenderTooltip
                  content={
                    <>
                      Enter the Transit MRN that is to be declared the customs entry to claim Preference.
                      <br />
                      Enter NA to confirm the goods do not require a Transit MRN to claim Preference.
                    </>
                  }
                  minWidth={350}
                />
              }
            >
              {({ invoiceNumber, origin }) => {
                const transitValue = getPreferenceValue(preferenceData, invoiceNumber, origin, 'directTransRule');
                const transitError = getPreferenceValue(preferenceData, invoiceNumber, origin, 'directTransRuleError');
                const isRequired = true;
                const isFormatValid = !transitValue || getIsValidPreferenceTransitMrn(transitValue);
                const showError = transitError || (transitValue && !isFormatValid);

                return (
                  <div className="rex-number-field">
                    <input
                      name="directTransRule"
                      type="text"
                      data-testid={rowTestId('transitMrn', origin, invoiceNumber)}
                      placeholder="Transit MRN"
                      onChange={(e) => {
                        handlePreferenceChange(e, invoiceNumber, origin);
                      }}
                      className={showError || (!transitValue && isRequired) ? 'qa-field-error' : ''}
                      value={transitValue || ''}
                      disabled={isReadOnly}
                    />
                    {showError && !!transitValue && (
                      <div className="validate-rex-error">
                        <Warning errorMessage={TRANSIT_MRN_VALIDATION_MESSAGE} />
                      </div>
                    )}
                  </div>
                );
              }}
            </DataTable.Column>
          )}
        </DataTable>
      </Modal.Body>
    </>
  );
};

const PreferenceCheckModal = ({
  countries,
  products,
  isOpen,
  onClose,
  setIsPreferenceCheckValid,
  exchangeRate,
  isReadOnly,
}) => {
  const store = useContext(StoreContext);

  const { staticDropdowns } = useDropdownOptions();
  const { validateRex } = useQaItemsActions();
  const { preferenceClaimOptions } = staticDropdowns.data || {};

  // country data and options
  const euCountries = countries.euCountries || [];
  const thirdCountries = countries.thirdCountries || [];
  const euPreferenceOptions = preferenceClaimOptions?.filter((option) => option.areacodeValue === 'EU') || [];
  const thirdCountryPreferenceOptions =
    preferenceClaimOptions?.filter((option) => option.areacodeValue === 'ROW') || [];

  // table handles
  const euDataHandle = useDataTableHandle();
  const thirdDataHandle = useDataTableHandle();

  const normalisedProducts = products.map((product) => ({
    ...product,
    invoiceNumber: product.invoiceNumber || store.invoicenumber[0].toUpperCase() || 'N/A',
    value: product.itemValue || product.value || 0,
  }));

  // get only products which has preference YES
  const preferenceProducts = normalisedProducts.filter(
    (product) => String(product.pref) === '1' || product.preferential === 'YES' || product.preference === 'YES'
  );

  // group products by invoiceNumber and origin, summing values for each unique combination
  const uniqueInvoices = [
    ...new Set(preferenceProducts.map((product) => `${product.invoiceNumber}||${product.origin}`)),
  ].map((item) => {
    const [invoiceNumber, origin] = item.split('||');

    const value = calculateInvoiceTotal(normalisedProducts, invoiceNumber);
    const statValue = convertToGbp(value, exchangeRate);

    return { invoiceNumber, origin, value: statValue?.toFixed(2) || '0.00' };
  });

  // split unique invoices into two arrays
  const euCountryInvoices = uniqueInvoices.filter((invoice) => euCountries.includes(invoice.origin));
  const thirdCountryInvoices = uniqueInvoices.filter((invoice) => thirdCountries.includes(invoice.origin));

  // storing all data here - when user Saves this will be set in the store
  const [payload, setPayload] = useState([]);

  const handlePreferenceChange = (event, invoiceNumber, origin) => {
    const newPayloadData = payload.map((row) => {
      if (row.invoiceNumber === invoiceNumber && row.origin === origin) {
        let fieldValue = event.target.value;
        if (event.target.name === 'directTransRule') {
          fieldValue = fieldValue.toUpperCase();
        }

        const newRow = { ...row, [event.target.name]: fieldValue };

        if (event.target.name === 'directTransRule') {
          newRow.directTransRuleError = fieldValue ? !getIsValidPreferenceTransitMrn(fieldValue) : false;
        }

        if (
          event.target.name === 'EUPreferenceClaim' &&
          isEuImportersKnowledgeClaim(event.target.value, euPreferenceOptions)
        ) {
          newRow.rexNumber = null;
          newRow.rexError = false;
        }

        return newRow;
      }
      return row;
    });
    setPayload(newPayloadData);
  };

  useEffect(() => {
    if (isOpen) {
      const initialData = [...store.preferenceData[0]];

      for (const invoice of uniqueInvoices) {
        const hasData = initialData.some(
          (data) => data.invoiceNumber === invoice.invoiceNumber && data.origin === invoice.origin
        );
        if (!hasData) {
          initialData.push({ invoiceNumber: invoice.invoiceNumber, origin: invoice.origin });
        }
      }
      setPayload([...initialData]);
    }
  }, [isOpen]);

  const onSave = async () => {
    const clearedPayload = payload.map((row) => {
      if (isEuImportersKnowledgeClaim(row.EUPreferenceClaim, euPreferenceOptions)) {
        return { ...row, rexNumber: null, rexError: false };
      }
      return row;
    });

    const rowsWithRex = clearedPayload.filter((row) => !!row.rexNumber);
    const data = rowsWithRex.length ? await validateRex.mutateAsync(rowsWithRex) : { items: [] };

    const newPayload = clearedPayload.map((row) => {
      const updatedRow = { ...row };

      if (row.thirdPreferenceClaim && row.directTransRule) {
        updatedRow.directTransRuleError = !getIsValidPreferenceTransitMrn(row.directTransRule);
      }

      data.items.forEach((validation) => {
        if (validation.rexNumber === updatedRow.rexNumber) {
          updatedRow.rexError = validation.valid ? false : true;
        }
      });
      return updatedRow;
    });
    setPayload(newPayload);
    store.preferenceData[1](newPayload);

    const preferenceDataIsValid = validatePreferenceData(
      newPayload,
      store.QAProductsList[0],
      store.invoicenumber[0],
      exchangeRate,
      preferenceClaimOptions
    );
    setIsPreferenceCheckValid(preferenceDataIsValid);

    if (newPayload.some((row) => row.rexError || row.directTransRuleError)) {
      return;
    }
    onClose();
  };

  return (
    <Modal animation={false} show={isOpen} onHide={onClose} size="lg" centered className="qa-preference-check-modal">
      {!!countries.euCountries.length && (
        <RenderTable
          title="EU Preference"
          dataTableHandle={euDataHandle}
          idKey="eu-country-preference"
          data={euCountryInvoices}
          preferenceOptions={euPreferenceOptions}
          preferenceData={payload}
          handlePreferenceChange={handlePreferenceChange}
          isReadOnly={isReadOnly}
        />
      )}

      {!!countries.thirdCountries.length && (
        <RenderTable
          title="3rd Country Preference"
          dataTableHandle={thirdDataHandle}
          idKey="third-country-preference"
          data={thirdCountryInvoices}
          preferenceOptions={thirdCountryPreferenceOptions}
          preferenceData={payload}
          handlePreferenceChange={handlePreferenceChange}
          isReadOnly={isReadOnly}
        />
      )}
      <Modal.Footer>
        <div className="d-flex w-100 justify-content-between">
          <span></span>
          {isReadOnly ? (
            <button className="epo-continue-button small-button" type="button" onClick={onClose}>
              Close
            </button>
          ) : (
            <button className="epo-continue-button small-button" type="button" onClick={onSave}>
              Save
            </button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default PreferenceCheckModal;
